import { useState } from "react";
import { eur, num, showMoney } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { ExpandToggle } from "@/components/ExpandToggle";
import { Tips, Stat } from "@/components/marketing/MarketingTips";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const TYPE = { rmk: "Retargeting", acquisti: "Acquisti", post: "Post sponsorizzato", altro: "Traffico / notorietà" };
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const monthLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;
const shortDate = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(2, 4)}`;

const CampaignRow = ({ c, i, m }) => (
  <TableRow data-testid={`marketing-campaign-${i}`}>
    <TableCell className="max-w-[320px]">
      <p className="truncate text-sm font-medium" title={c.name}>{c.name}</p>
      <p className="text-xs text-muted-foreground">{TYPE[c.type]} · {shortDate(c.from)} – {shortDate(c.to)}</p>
    </TableCell>
    {m && <TableCell className="text-right text-sm">{eur(c.spend)}</TableCell>}
    <TableCell className="text-right text-sm">{num(c.link_clicks)}{m && c.cpc != null && <span className="block text-xs text-muted-foreground">{eur(c.cpc)}/clic</span>}</TableCell>
    <TableCell className="text-right text-sm">{num(c.purchases)}</TableCell>
    {m && <TableCell className="text-right text-sm font-semibold">{eur(c.value)}</TableCell>}
    {m && <TableCell className="text-right">
      <Badge variant="outline" className={c.roas >= 3 ? "border-emerald-500/50 text-emerald-400" : c.roas > 0 ? "border-amber-500/50 text-amber-400" : "border-rose-500/50 text-rose-400"}>{c.roas ? c.roas.toFixed(1) : "0"}</Badge>
    </TableCell>}
  </TableRow>
);

const Monthly = ({ rows, m }) => (
  <div className="overflow-x-auto" data-testid="marketing-ads-monthly">
    <p className="mb-2 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{m ? "Spesa pubblicitaria e fatturato online per mese" : "Nuove clienti per mese"}</p>
    <div className="flex min-w-max gap-2">
      {rows.map((r) => (
        <div key={r.month} className="w-24 rounded-md border border-border/60 p-2 text-xs">
          <p className="font-semibold">{monthLabel(r.month)}</p>
          {m && <p className="text-muted-foreground">Ads {eur(r.spend)}</p>}
          {m && <p>Fatt. {eur(r.revenue)}</p>}
          <p className="text-muted-foreground">{num(r.new_customers)} nuovi</p>
        </div>
      ))}
    </div>
  </div>
);

export const MarketingAds = ({ ads }) => {
  const [all, setAll] = useState(false);
  const t = ads.totals;
  const m = showMoney();
  return (
    <div className="space-y-5" data-testid="marketing-ads">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {m && <Stat label="Spesa 12 mesi" value={eur(t.spend)} testid="marketing-ads-spend" />}
        {m && <Stat label="Vendite attribuite" value={eur(t.value)} testid="marketing-ads-value" />}
        <Stat label="Acquisti" value={num(t.purchases)} testid="marketing-ads-purchases" />
        {m && <Stat label="ROAS medio" value={t.roas.toFixed(1)} testid="marketing-ads-roas" />}
      </div>
      <Tips tips={ads.tips} testid="marketing-ads-tips" />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Campagna</TableHead>{m && <TableHead className="text-right">Spesa</TableHead>}<TableHead className="text-right">Clic al sito</TableHead>
              <TableHead className="text-right">Acquisti</TableHead>{m && <TableHead className="text-right">Vendite</TableHead>}{m && <TableHead className="text-right">ROAS</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>{(all ? ads.campaigns : ads.campaigns.slice(0, 8)).map((c, i) => <CampaignRow key={c.id} c={c} i={i} m={m} />)}</TableBody>
        </Table>
      </div>
      <ExpandToggle total={ads.campaigns.length} open={all} onToggle={() => setAll(!all)} testid="marketing-campaigns-toggle" />
      <Monthly rows={ads.monthly} m={m} />
    </div>
  );
};
